import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  ChevronRight, 
  ArrowLeft,
  ShieldCheck,
  Receipt,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/Logo";

/**
 * PaymentResult Component
 * 
 * Esta página es el punto de retorno tras el pago en Wompi.
 * Consulta directamente la tabla 'wompi_transactions' en Supabase.
 * El backend (app/api/v1/payments.py) actualiza esta tabla vía Webhook 
 * cuando Wompi confirma el estado del pago.
 */
const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const transactionId = searchParams.get("id");
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [transaction, setTransaction] = useState<any>(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!transactionId) {
      setLoading(false);
      return;
    }

    const checkStatus = async () => {
      try {
        // 1. Intentar verificación activa vía Backend API de Wompi
        const apiBase = import.meta.env.VITE_API_BASE_URL || "http://localhost:8001";
        const verifyRes = await fetch(`${apiBase}/api/v1/payments/wompi/verify/${transactionId}`);
        if (verifyRes.ok) {
          const json = await verifyRes.json();
          if (json.transaction) {
            setTransaction(json.transaction);
            setStatus(json.transaction.status);
            setLoading(false);
            if (json.transaction.status === "APPROVED") {
              window.dispatchEvent(new CustomEvent("credits-updated"));
            }
            return;
          }
        }
      } catch (backendErr) {
        console.warn("Backend verify endpoint unreachable, falling back to direct Supabase lookup:", backendErr);
      }

      try {
        // 2. Fallback a consulta direct a Supabase (sin join roto)
        const { data, error } = await supabase
          .from("wompi_transactions")
          .select("*")
          .or(`wompi_id.eq.${transactionId},reference.eq.${transactionId}`)
          .maybeSingle();

        if (data) {
          if (data.user_id && !data.profiles) {
            try {
              const { data: prof } = await supabase
                .from("profiles")
                .select("full_name, email")
                .eq("id", data.user_id)
                .maybeSingle();
              if (prof) data.profiles = prof;
            } catch (pErr) {
              console.warn("Could not fetch profile:", pErr);
            }
          }

          setTransaction(data);
          setStatus(data.status);

          if (data.status !== "PENDING" || attempts >= 15) {
            setLoading(false);
            if (data.status === "APPROVED") {
              window.dispatchEvent(new CustomEvent("credits-updated"));
            }
          } else {
            setTimeout(() => setAttempts(prev => prev + 1), 3000);
          }
        } else if (attempts < 15) {
          setTimeout(() => setAttempts(prev => prev + 1), 3000);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching transaction:", err);
        if (attempts >= 15) setLoading(false);
      }
    };

    checkStatus();
  }, [transactionId, attempts]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <Logo className="scale-125 transition-transform duration-700 animate-pulse" />
          </div>
          <div className="relative pt-4">
            <Loader2 className="h-12 w-12 animate-spin text-[#1B3B6F] mx-auto" />
            <ShieldCheck className="h-5 w-5 text-blue-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-2" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-slate-900">Verificando tu pago</h2>
            <p className="text-slate-500 max-w-xs mx-auto text-sm">
              Estamos confirmando la transacción con Wompi de forma segura.
              Esto puede tardar unos segundos...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isApproved = status === "APPROVED";
  const isDeclined = status === "DECLINED" || status === "VOIDED" || status === "ERROR";
  const isPending = !isApproved && !isDeclined;

  const formattedDate = transaction?.created_at 
    ? new Date(transaction.created_at).toLocaleString('es-ES', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      }).replace(',', ' -')
    : new Date().toLocaleString('es-ES', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      }).replace(',', ' -');

  const amount = transaction?.amount_in_cents ? (transaction.amount_in_cents / 100).toLocaleString('es-CO') : null;

  return (
    <div className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h2 className={`text-2xl font-bold ${isApproved ? 'text-green-600' : isDeclined ? 'text-red-600' : 'text-amber-600'}`}>
            {isApproved ? "¡Pago exitoso!" : isDeclined ? "¡Pago no completado!" : "Confirmación en proceso..."}
          </h2>
          <div className={`text-5xl font-black mt-2 ${isApproved ? 'text-green-600' : isDeclined ? 'text-red-500' : 'text-slate-700'}`}>
            {amount ? `$${amount}` : "$14,900"}
          </div>
          <p className="text-slate-500 mt-2 font-medium">
            {formattedDate}
          </p>
        </div>

        <div className={`mt-8 mb-10 p-8 rounded-[2rem] text-center border ${
          isApproved 
            ? 'bg-green-50 border-green-100 text-green-800' 
            : isDeclined
            ? 'bg-red-50 border-red-100 text-red-800'
            : 'bg-amber-50 border-amber-100 text-amber-900'
        }`}>
          <div className="space-y-2 text-sm md:text-base font-medium">
            <p className="mb-1">
              {transaction?.status_message || (
                isApproved 
                  ? "Tu pago ha sido confirmado y tus créditos han sido activados." 
                  : isDeclined 
                  ? "La transacción fue rechazada o no pudo ser completada."
                  : "Estamos en proceso de confirmación con tu banco."
              )}
            </p>
            {isDeclined && <p className="font-bold">No hemos debitado ningún dinero de tu cuenta.</p>}
            {isPending && <p className="text-sm text-amber-800">Si tu pago fue exitoso en Wompi, la acreditación se completará automáticamente en breve.</p>}
            <p>Comprobante o notificación enviada a: <span className="font-bold">{transaction?.profiles?.email || transaction?.customer_data?.email || "tu correo"}</span></p>
          </div>
        </div>

        <div className="space-y-10 px-4 md:px-8">
          {/* Información de la transacción */}
          <section>
            <h3 className="text-lg font-bold text-slate-800 mb-6">Información de la transacción</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <span className="text-slate-500 font-medium">Transacción #</span>
                <span className="text-slate-700 font-medium text-right max-w-[60%] break-all">{transactionId}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-slate-500 font-medium">Referencia</span>
                <span className="text-slate-700 font-medium text-right max-w-[60%] break-all">{transaction?.reference || "N/A"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500 font-medium">Método de pago</span>
                <div className="flex items-center gap-2">
                  <span className="text-slate-700 font-medium uppercase">{transaction?.payment_method_type || "No especificado"}</span>
                  {transaction?.payment_method_data?.last_four && (
                    <span className="text-slate-700 font-medium ml-2">**** {transaction.payment_method_data.last_four}</span>
                  )}
                </div>
              </div>
            </div>
          </section>

          <div className="h-px bg-slate-200 w-full" />

          {/* Información del pagador */}
          <section>
            <h3 className="text-lg font-bold text-slate-800 mb-6">Información del pagador</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-500 font-medium">Nombre</span>
                <span className="text-slate-700 font-medium">{transaction?.profiles?.full_name || transaction?.customer_data?.full_name || "N/A"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500 font-medium">Teléfono</span>
                <span className="text-slate-700 font-medium">{transaction?.customer_data?.phone || transaction?.customer_data?.phone_number || transaction?.profiles?.phone || "N/A"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500 font-medium">Email</span>
                <span className="text-slate-700 font-medium">{transaction?.profiles?.email || transaction?.customer_data?.email || "N/A"}</span>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 flex flex-col gap-4 px-4 md:px-8 no-print">
          {isPending && (
            <Button 
              className="w-full h-14 rounded-2xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-lg transition-all"
              onClick={() => {
                setLoading(true);
                setAttempts(0);
              }}
            >
              Reverificar Estado del Pago
            </Button>
          )}

          <Button 
            className="w-full h-14 rounded-2xl bg-[#1B3B6F] hover:bg-[#132a50] text-white font-bold text-lg transition-all"
            onClick={() => navigate("/dashboard")}
          >
            Ir al Panel de Control
          </Button>
          
          <Button 
            variant="ghost" 
            className="w-full h-12 rounded-2xl text-slate-400 hover:bg-slate-50 font-semibold"
            onClick={() => window.print()}
          >
            Descargar Comprobante PDF
          </Button>
        </div>

        <div className="mt-12 text-center no-print">
          <div className="flex items-center justify-center gap-2 text-slate-300 text-[10px] font-bold uppercase tracking-widest mb-4">
            <ShieldCheck className="h-3 w-3" /> Transacción Segura por Wompi
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
